import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { addDays, formatDe, labelFor, todayIn } from '@frisk/shared';
import { useCameraScanner } from '../hooks/useCameraScanner';
import { t } from '../theme';

/**
 * The paired-phone view, served at /s/<token>.
 *
 * A separate screen from the panel, not a reskin: this is held in one hand,
 * in portrait, usually with a shopping bag in the other. It never renders the
 * rail, never loads household data, and its token can do nothing but look up
 * products and put items away.
 */
const TOKEN_KEY = 'frisk.scanToken';
const QUICK_DAYS = [3, 7, 14, 30];

type Phase = 'claiming' | 'scanning' | 'confirm' | 'naming' | 'dead';

interface Draft {
  barcode: string;
  name: string;
  imageUrl: string | null;
  /** Civil date, or null for "kein Datum". Presets just write into this. */
  bestBefore: string | null;
  /** Six identical yoghurts are one trip through this form, not six. */
  qty: number;
}

async function scanFetch<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/scan${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (res.status === 204) return undefined as T;
  const body = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (!res.ok) throw Object.assign(new Error(body?.message ?? res.statusText), { status: res.status });
  return body as T;
}

export function PhoneScanner() {
  const { token: tokenParam } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('claiming');
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [added, setAdded] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // --- claim ------------------------------------------------------------

  useEffect(() => {
    const fromUrl = tokenParam ?? null;
    const stored = sessionStorage.getItem(TOKEN_KEY);
    const candidate = fromUrl || stored;

    if (!candidate) {
      setPhase('dead');
      setError('Kein Kopplungs-Code. QR-Code am Panel erneut anzeigen.');
      return;
    }

    void (async () => {
      if (fromUrl && fromUrl !== stored) {
        try {
          await fetch('/api/scan/claim', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: fromUrl }),
          }).then(async (r) => {
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message ?? 'Kopplung fehlgeschlagen');
          });
          sessionStorage.setItem(TOKEN_KEY, fromUrl);
        } catch (err) {
          setPhase('dead');
          setError(err instanceof Error ? err.message : 'Kopplung fehlgeschlagen');
          return;
        }
      }
      // Keep the token out of the address bar and the browser history.
      navigate('/s/', { replace: true });
      setToken(candidate);
      setPhase('scanning');
    })();
    // Claiming runs once for the token this view was opened with; re-running
    // it on the scrub navigation would try to claim an already-spent token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- scanning ---------------------------------------------------------

  const onBarcode = useCallback(
    async (barcode: string) => {
      if (!token || busy) return;
      setBusy(true);
      try {
        const product = await scanFetch<{ name: string; imageUrl: string | null }>(
          token,
          `/product/${barcode}`,
        );
        setDraft({ barcode, name: product.name, imageUrl: product.imageUrl, bestBefore: addDays(today, 7), qty: 1 });
        setPhase('confirm');
        navigator.vibrate?.(40);
      } catch (err) {
        const status = (err as { status?: number }).status;
        if (status === 404) {
          setDraft({ barcode, name: '', imageUrl: null, bestBefore: addDays(today, 7), qty: 1 });
          setPhase('naming');
          navigator.vibrate?.([30, 60, 30]);
        } else if (status === 401) {
          setPhase('dead');
          setError('Kopplung abgelaufen — QR-Code am Panel erneut anzeigen');
        } else {
          setError(err instanceof Error ? err.message : 'Suche fehlgeschlagen');
        }
      } finally {
        setBusy(false);
      }
    },
    [token, busy],
  );

  const camera = useCameraScanner((code) => void onBarcode(code));
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  useEffect(() => {
    if (phase !== 'scanning') {
      cameraRef.current.pause();
      return;
    }
    if (cameraRef.current.state === 'live') cameraRef.current.resume();
    else void cameraRef.current.start();
  }, [phase]);

  // --- saving -----------------------------------------------------------

  const save = async () => {
    if (!token || !draft || busy) return;
    setBusy(true);
    try {
      if (phase === 'naming') {
        await scanFetch(token, `/product/${draft.barcode}`, {
          method: 'PUT',
          body: JSON.stringify({ name: draft.name.trim() }),
        });
      }
      await scanFetch(token, '/item', {
        method: 'POST',
        body: JSON.stringify({
          name: draft.name.trim(),
          barcode: draft.barcode,
          bestBefore: draft.bestBefore,
          qty: draft.qty,
        }),
      });
      setAdded((a) => [draft.qty > 1 ? `${draft.qty}× ${draft.name.trim()}` : draft.name.trim(), ...a].slice(0, 30));
      setDraft(null);
      setPhase('scanning');
      navigator.vibrate?.(20);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  // --- chrome -----------------------------------------------------------

  /*
   * A fixed, scrollable layer rather than a tall block.
   *
   * global.css pins html/body/#root to 100dvh with overflow:hidden — correct
   * for a wall panel that must never scroll, fatal on a phone, where it left
   * the "Einräumen" button rendered below the fold and unreachable. A fixed
   * container scrolls its own content regardless of what the body does.
   */
  const shell = (children: React.ReactNode) => (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: t.bgGradient,
        color: t.ink,
        fontFamily: t.sans,
        display: 'flex',
        flexDirection: 'column',
        padding: 18,
        // Safari's bottom bar overlaps the viewport; without this the last
        // control sits underneath it.
        paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 0px) + 44px)',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flex: 'none' }}>
        <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.035em' }}>
          frisk<span style={{ color: t.accent }}>.</span>
        </div>
        <div style={{ fontFamily: t.mono, fontSize: 11, letterSpacing: '0.14em', color: t.inkFaint }}>
          HANDY-SCANNER
        </div>
        <div style={{ flex: 1 }} />
        {added.length > 0 && (
          <div style={{ fontSize: 15, color: t.accent }}>{added.length} eingeräumt</div>
        )}
      </div>
      {children}
    </div>
  );

  const bigButton = (label: string, onTap: () => void, disabled = false) => (
    <div
      className={disabled ? undefined : 'press'}
      onClick={() => !disabled && onTap()}
      style={{
        height: 66,
        borderRadius: 18,
        background: t.accent,
        color: t.accentInk,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        fontWeight: 600,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </div>
  );

  if (phase === 'claiming') return shell(<div style={{ fontSize: 20, color: t.inkMuted }}>Verbinde …</div>);

  if (phase === 'dead')
    return shell(
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 40 }}>
        <div style={{ fontSize: 26, fontWeight: 600 }}>Nicht verbunden</div>
        <div style={{ fontSize: 18, color: t.inkMuted, textWrap: 'pretty' }}>{error}</div>
      </div>,
    );

  return shell(
    <>
      {/* Kept mounted, never unmounted: the hook holds videoRef, and tearing
          the element out would break resuming. Hidden during confirm because
          a 3:4 viewfinder is most of a phone screen and the confirm step is a
          form, not a camera. */}
      <div
        style={{
          display: phase === 'scanning' ? 'block' : 'none',
          position: 'relative',
          borderRadius: 22,
          overflow: 'hidden',
          background: 'oklch(0.13 0.01 60)',
          border: t.hairline,
          aspectRatio: '3 / 4',
          flex: 'none',
        }}
      >
        <video
          ref={camera.videoRef}
          muted
          playsInline
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: camera.state === 'live' ? 1 : 0,
          }}
        />
        {/* the band the decoder actually reads */}
        <div
          style={{
            position: 'absolute',
            left: '5%',
            right: '5%',
            top: '22.5%',
            bottom: '22.5%',
            border: `2px solid ${t.accent}`,
            borderRadius: 14,
            pointerEvents: 'none',
          }}
        />
        {camera.state !== 'live' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 24,
              fontSize: 17,
              color: camera.error ? 'oklch(0.82 0.14 30)' : t.inkMuted,
            }}
          >
            {camera.error ?? 'Kamera startet …'}
          </div>
        )}
        {camera.state === 'live' && (
          <div
            style={{
              position: 'absolute',
              left: 12,
              right: 12,
              bottom: 12,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Zoom first: too few pixels per bar is the usual reason a code
                will not read, and nothing downstream can recover from it. */}
            {camera.zoomRange && (
              <>
                <div
                  className="press"
                  onClick={() =>
                    void camera.setZoom(Math.max(camera.zoomRange!.min, camera.zoom - camera.zoomRange!.step * 4))
                  }
                  style={pill}
                >
                  −
                </div>
                <div style={{ ...pill, minWidth: 58, textAlign: 'center' }}>{camera.zoom.toFixed(1)}×</div>
                <div
                  className="press"
                  onClick={() =>
                    void camera.setZoom(Math.min(camera.zoomRange!.max, camera.zoom + camera.zoomRange!.step * 4))
                  }
                  style={pill}
                >
                  +
                </div>
              </>
            )}
            {camera.torchAvailable && (
              <div
                className="press"
                onClick={() => void camera.toggleTorch()}
                style={{
                  ...pill,
                  background: camera.torch ? t.accent : 'oklch(0.2 0.012 60 / 0.85)',
                  color: camera.torch ? t.accentInk : t.ink,
                }}
              >
                Licht
              </div>
            )}
            <div style={{ flex: 1 }} />
            {camera.cameras.length > 1 && (
              <div className="press" onClick={() => void camera.switchCamera()} style={pill}>
                Kamera
              </div>
            )}
          </div>
        )}
      </div>

      {phase === 'scanning' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
          <div style={{ fontSize: 18, color: t.inkMuted, textAlign: 'center' }}>
            {busy ? 'Suche …' : 'Barcode in den Rahmen halten'}
          </div>
          {/* The two numbers that tell you whether a stubborn scan is a bad
              angle or a camera handing back 640×480. Requested resolution is
              a wish, not a promise, so the delivered one has to be visible. */}
          {camera.state === 'live' && (
            <div
              style={{
                fontFamily: t.mono,
                fontSize: 11,
                letterSpacing: '0.06em',
                color: 'oklch(0.5 0.012 60)',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              {camera.resolution ?? '—'} → {camera.roiSize ?? '—'} · {camera.frames} Versuche
              <br />
              {/* Brightness of the region actually decoded. Near zero means no
                  frames are reaching the canvas at all, which is a different
                  problem from a barcode that will not read. */}
              Helligkeit {camera.luma ?? '—'}
              {camera.lastError ? ` · Fehler: ${camera.lastError}` : ''}
            </div>
          )}
        </div>
      )}

      {(phase === 'confirm' || phase === 'naming') && draft && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div
              style={{
                width: 62,
                height: 62,
                flex: 'none',
                borderRadius: 14,
                background: draft.imageUrl
                  ? `center / cover no-repeat url(${draft.imageUrl})`
                  : 'repeating-linear-gradient(135deg, oklch(0.3 0.012 60) 0 8px, oklch(0.26 0.012 60) 8px 16px)',
              }}
            />
            {phase === 'naming' ? (
              <input
                ref={nameRef}
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Was ist das?"
                enterKeyHint="done"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'oklch(0.2 0.012 60)',
                  border: '1px solid oklch(1 0 0 / 0.12)',
                  borderRadius: 14,
                  padding: '14px 16px',
                  color: t.ink,
                  fontSize: 20,
                  fontWeight: 600,
                  outline: 'none',
                }}
              />
            ) : (
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', minWidth: 0 }}>
                {draft.name}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {QUICK_DAYS.map((d) => {
              const value = addDays(today, d);
              return (
                <div
                  key={d}
                  className="press"
                  onClick={() => setDraft({ ...draft, bestBefore: value })}
                  style={{
                    padding: '12px 16px',
                    borderRadius: 999,
                    fontSize: 17,
                    fontWeight: 500,
                    background: draft.bestBefore === value ? 'oklch(0.32 0.012 60)' : t.surface,
                    border: draft.bestBefore === value ? `1px solid ${t.accent}` : t.hairline,
                  }}
                >
                  {labelFor(d)}
                </div>
              );
            })}
            <div
              className="press"
              onClick={() => setDraft({ ...draft, bestBefore: null })}
              style={{
                padding: '12px 16px',
                borderRadius: 999,
                fontSize: 17,
                fontWeight: 500,
                background: draft.bestBefore === null ? 'oklch(0.32 0.012 60)' : t.surface,
                border: draft.bestBefore === null ? `1px solid ${t.accent}` : t.hairline,
              }}
            >
              kein Datum
            </div>
          </div>

          {/* Buying six of something is one scan, not six. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 16, color: t.inkMuted, flex: 1 }}>Anzahl</span>
            <div
              className="press"
              onClick={() => setDraft({ ...draft, qty: Math.max(1, draft.qty - 1) })}
              style={qtyBtn}
            >
              −
            </div>
            <div style={{ minWidth: 58, textAlign: 'center', fontSize: 26, fontWeight: 600 }}>{draft.qty}×</div>
            <div
              className="press"
              onClick={() => setDraft({ ...draft, qty: Math.min(99, draft.qty + 1) })}
              style={qtyBtn}
            >
              +
            </div>
          </div>

          {/* The presets cover the common cases; this covers the printed one.
              A native date input is the iOS wheel picker — far better than
              anything I would draw. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 16, color: t.inkMuted }}>
            <span style={{ whiteSpace: 'nowrap' }}>genaues Datum</span>
            <input
              type="date"
              value={draft.bestBefore ?? ''}
              min={today}
              onChange={(e) => setDraft({ ...draft, bestBefore: e.target.value || null })}
              style={{
                flex: 1,
                minWidth: 0,
                background: 'oklch(0.2 0.012 60)',
                border: '1px solid oklch(1 0 0 / 0.12)',
                borderRadius: 12,
                padding: '12px 14px',
                color: t.ink,
                fontSize: 17,
                fontFamily: t.sans,
                outline: 'none',
              }}
            />
          </label>

          <div style={{ fontSize: 15, color: t.inkFaint }}>
            {draft.bestBefore ? `haltbar bis ${formatDe(draft.bestBefore)}` : 'ohne Ablaufdatum'}
          </div>

          {bigButton(
            busy
              ? 'Speichert …'
              : !draft.name.trim()
                ? 'Erst benennen'
                : draft.qty > 1
                  ? `${draft.qty}× einräumen`
                  : 'Einräumen',
            () => void save(),
            busy || !draft.name.trim(),
          )}
          <div
            className="press"
            onClick={() => {
              setDraft(null);
              setPhase('scanning');
            }}
            style={{ textAlign: 'center', padding: 10, fontSize: 17, color: t.inkMuted }}
          >
            Abbrechen
          </div>
        </div>
      )}

      {error && (
        <div
          className="press"
          onClick={() => setError(null)}
          style={{
            padding: '12px 16px',
            borderRadius: 14,
            background: 'oklch(0.28 0.06 30)',
            border: '1px solid oklch(0.72 0.17 25 / 0.5)',
            fontSize: 16,
            color: 'oklch(0.9 0.1 30)',
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      {added.length > 0 && phase === 'scanning' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto' }}>
          {added.map((name, i) => (
            <div
              key={`${name}-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 14,
                background: t.surface,
                fontSize: 17,
                opacity: 1 - Math.min(0.6, i * 0.12),
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: 999, background: t.accent }} />
              {name}
            </div>
          ))}
        </div>
      )}
    </>,
  );
}

const pill: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 999,
  background: 'oklch(0.2 0.012 60 / 0.85)',
  backdropFilter: 'blur(8px)',
  fontSize: 15,
  fontWeight: 500,
  flex: 'none',
};

const qtyBtn: React.CSSProperties = {
  width: 58,
  height: 58,
  flex: 'none',
  borderRadius: 16,
  background: 'oklch(0.3 0.012 60)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 28,
  fontWeight: 500,
};
